import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { fetchTimeLine, updateDeliveryStatus } from "@/store/slices/orderSlice";
import useHasPermission from "@/components/useHasPermission";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import moment from "moment";
import { useSocket } from "@/components/SocketContext.jsx";
import api from "@/services/api";
import {
  ADMIN_GET_DP_CURRENT_LOCATION_PATH,
  SOCKET_DP_LOCATION_ADMIN_EVENT,
  deliveryPartnerMatchesLocationPayload,
  parseLocationPayloadLatLng,
} from "@/constants/deliveryLocationSocket";
import { useTranslation } from "react-i18next";

const GOOGLE_SCRIPT_ID = "google-maps-directions-js";

/** API may return a bare array or an object wrapper. */
function normalizeTimelineArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.tracking)) return raw.tracking;
  if (Array.isArray(raw?.timeline)) return raw.timeline;
  return [];
}

function extractTrackingMapPoints(entries) {
  return entries
    .map((e) => ({
      lat: fmtLatLng(e.latitude ?? e.lat ?? e.delivery_latitude),
      lng: fmtLatLng(e.longitude ?? e.lng ?? e.delivery_longitude),
    }))
    .filter((p) => p.lat != null && p.lng != null);
}

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.DirectionsService) {
      resolve();
      return;
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing) {
      const done = () => resolve();
      const fail = () =>
        reject(new Error("Google Maps failed to load for directions"));
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", fail, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Google Maps failed to load for directions"));
    document.head.appendChild(script);
  });
}

function fmtLatLng(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n;
}

const timelineMap = {
  picked: 1,
  transit: 2,
  arrived: 3,
  verification: 4,
  review: 5,
  delivered: 6,
};

export default function ViewDeliveryTimelineRouteModal({
  onClose,
  orderId,
  vendor = null,
  deliveryPartner = null,
  customer = null
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation('order');
  const { t: ct } = useTranslation('common');
  const { timeline, loading } = useSelector((state) => state.order);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const trackingPolylineRef = useRef(null);
  const dbMarkerRef = useRef(null);
  const vendorMarkerRef = useRef(null);
  const clientMarkerRef = useRef(null);
  const liveMarkerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const retryRef = useRef(0);
  const socket = useSocket();
  const [mapError, setMapError] = useState("");
  const [mapRouteNonce, setMapRouteNonce] = useState(0);
  const [liveDpLatLng, setLiveDpLatLng] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [timelineOverride, setTimelineOverride] = useState(null);
  const hasDeliveryUpdatePermission = useHasPermission(`assign-delivery-partner.update`);

  const actionMenuOptions = useMemo(
    () => [{ label: t('reviewed_and_delivered_action'), value: "delivered" }],
    []
  );

  const googleKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!orderId) return;
    dispatch(fetchTimeLine(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    setLiveDpLatLng(null);
  }, [orderId, deliveryPartner?.id]);

  useEffect(() => {
    if (!socket) return;
    if (deliveryPartner?.delivery_timeline === "delivered") return;

    const handler = (payload) => {
      const data = payload?.data ?? payload;
      if (!deliveryPartnerMatchesLocationPayload(deliveryPartner, data)) return;
      const { lat, lng } = parseLocationPayloadLatLng(data);
      if (lat == null || lng == null) return;
      setLiveDpLatLng({ lat, lng });
    };

    socket.on(SOCKET_DP_LOCATION_ADMIN_EVENT, handler);
    return () => {
      socket.off(SOCKET_DP_LOCATION_ADMIN_EVENT, handler);
    };
  }, [socket, deliveryPartner]);

  /**
   * One DB snapshot when opening the modal + again on socket reconnect (no polling loop).
   * Real-time marker movement should come from SOCKET_DP_LOCATION_ADMIN_EVENT.
   */
  useEffect(() => {
    if (deliveryPartner?.delivery_timeline === "delivered") return;
    const assignmentId = deliveryPartner?.id;
    const rfqPid = deliveryPartner?.rfq_product_id;
    if (assignmentId == null && rfqPid == null) return;

    let cancelled = false;
    const fetchSnapshot = async () => {
      try {
        const q =
          assignmentId != null
            ? `delivery_assigned_order_id=${encodeURIComponent(assignmentId)}`
            : `rfq_product_id=${encodeURIComponent(rfqPid)}`;
        const { data } = await api.get(
          `${ADMIN_GET_DP_CURRENT_LOCATION_PATH}?${q}`
        );
        if (cancelled) return;
        const { lat, lng } = parseLocationPayloadLatLng(data);
        if (lat != null && lng != null) {
          setLiveDpLatLng({ lat, lng });
        }
      } catch {
        /* misconfigured GET or shape; socket may still work */
      }
    };

    fetchSnapshot();

    const onConnect = () => {
      fetchSnapshot();
    };
    if (socket) {
      socket.on("connect", onConnect);
    }

    return () => {
      cancelled = true;
      if (socket) {
        socket.off("connect", onConnect);
      }
    };
  }, [
    deliveryPartner?.id,
    deliveryPartner?.rfq_product_id,
    deliveryPartner?.delivery_timeline,
    socket,
  ]);

  useEffect(() => {
    return () => {
      if (liveMarkerRef.current) {
        liveMarkerRef.current.setMap(null);
        liveMarkerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!liveDpLatLng) {
      if (liveMarkerRef.current) {
        liveMarkerRef.current.setVisible(false);
      }
      return;
    }
    if (!mapInstanceRef.current || !window.google?.maps?.Marker) return;
    const map = mapInstanceRef.current;
    if (!liveMarkerRef.current) {
      liveMarkerRef.current = new window.google.maps.Marker({
        map,
        position: liveDpLatLng,
        title: t('delivery_partner_label'),
        zIndex: 55,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#2563EB", // blue (distinct from vendor/client)
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
      });

      // Click tooltip
      liveMarkerRef.current.addListener("click", () => {
        if (!infoWindowRef.current) {
          infoWindowRef.current = new window.google.maps.InfoWindow();
        }
        infoWindowRef.current.setContent(t('delivery_partner_label'));
        infoWindowRef.current.open({ map, anchor: liveMarkerRef.current });
      });
    } else {
      liveMarkerRef.current.setPosition(liveDpLatLng);
      liveMarkerRef.current.setVisible(true);
    }
  }, [liveDpLatLng, mapRouteNonce]);

  const dbLocation = useMemo(() => {
    return {
      lat: fmtLatLng(deliveryPartner?.current_location?.latitude ?? deliveryPartner?.latitude),
      lng: fmtLatLng(deliveryPartner?.current_location?.longitude ?? deliveryPartner?.longitude),
    };
  }, [deliveryPartner?.current_location?.latitude, deliveryPartner?.current_location?.longitude]);

  const origin = useMemo(() => {
    return {
      lat: fmtLatLng(vendor?.latitude),
      lng: fmtLatLng(vendor?.longitude),
    };
  }, [vendor?.latitude, vendor?.longitude]);

  const destination = useMemo(() => {
    return {
      lat: fmtLatLng(customer?.latitude),
      lng: fmtLatLng(customer?.longitude),
    };
    
  }, [customer?.latitude, customer?.longitude]);

  const trackingEntries = useMemo(
    () => normalizeTimelineArray(timeline),
    [timeline]
  );

  const trackingMapPoints = useMemo(
    () => extractTrackingMapPoints(trackingEntries),
    [trackingEntries]
  );
  
  const containerHasSize = (el, { minW = 120, minH = 120 } = {}) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect?.();
    const w = rect?.width || 0;
    const h = rect?.height || 0;
    return w >= minW && h >= minH;
  };

  useEffect(() => {
    let cancelled = false;
    retryRef.current = 0;

    function clearTrackingPolyline() {
      if (trackingPolylineRef.current) {
        trackingPolylineRef.current.setMap(null);
        trackingPolylineRef.current = null;
      }
    }

    function clearStaticMarkers() {
      if (dbMarkerRef.current) {
        dbMarkerRef.current.setMap(null);
        dbMarkerRef.current = null;
      }
      if (vendorMarkerRef.current) {
        vendorMarkerRef.current.setMap(null);
        vendorMarkerRef.current = null;
      }
      if (clientMarkerRef.current) {
        clientMarkerRef.current.setMap(null);
        clientMarkerRef.current = null;
      }
    }

    function ensureStaticMarkers(map) {
      if (!window.google?.maps?.Marker) return;
      if (!infoWindowRef.current && window.google?.maps?.InfoWindow) {
        infoWindowRef.current = new window.google.maps.InfoWindow();
      }

      if (!dbMarkerRef.current && dbLocation.lat != null && dbLocation.lng != null) {
        dbMarkerRef.current = new window.google.maps.Marker({
          map,
          position: dbLocation,
          title: t('delivery_partner_label'),
          label: {
            text: "DP",
            color: "#ffffff",
            fontWeight: "700",
          },
          zIndex: 40,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#FF0000",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });

        dbMarkerRef.current.addListener("click", () => {
          if (!infoWindowRef.current) return;
          infoWindowRef.current.setContent(t('delivery_partner_label'));
          infoWindowRef.current.open({ map, anchor: dbMarkerRef.current });
        });
      } else if (dbMarkerRef.current) {
        if (dbLocation.lat != null && dbLocation.lng != null) {
          dbMarkerRef.current.setMap(map);
          dbMarkerRef.current.setPosition(dbLocation);
          dbMarkerRef.current.setTitle(t('delivery_partner_label'));
          dbMarkerRef.current.setVisible(true);
        } else {
          dbMarkerRef.current.setVisible(false);
        }
      }

      if (!vendorMarkerRef.current) {
        vendorMarkerRef.current = new window.google.maps.Marker({
          map,
          position: origin,
          title: t('vendor_label'),
          label: {
            text: "V",
            color: "#ffffff",
            fontWeight: "700",
          },
          zIndex: 40,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#F59E0B", // amber
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });

        vendorMarkerRef.current.addListener("click", () => {
          if (!infoWindowRef.current) return;
          infoWindowRef.current.setContent(t('vendor_label'));
          infoWindowRef.current.open({ map, anchor: vendorMarkerRef.current });
        });
      } else {
        vendorMarkerRef.current.setMap(map);
        vendorMarkerRef.current.setPosition(origin);
        vendorMarkerRef.current.setTitle(t('vendor_label'));
        vendorMarkerRef.current.setVisible(true);
      }

      if (!clientMarkerRef.current) {
        clientMarkerRef.current = new window.google.maps.Marker({
          map,
          position: destination,
          title: t('client_label'),
          label: {
            text: "C",
            color: "#ffffff",
            fontWeight: "700",
          },
          zIndex: 40,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#16A34A", // green (distinct from vendor/dp)
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });

        clientMarkerRef.current.addListener("click", () => {
          if (!infoWindowRef.current) return;
          infoWindowRef.current.setContent(t('client_label'));
          infoWindowRef.current.open({ map, anchor: clientMarkerRef.current });
        });
      } else {
        clientMarkerRef.current.setMap(map);
        clientMarkerRef.current.setPosition(destination);
        clientMarkerRef.current.setTitle(t('client_label'));
        clientMarkerRef.current.setVisible(true);
      }
    }

    async function initMapAndRoute() {
      setMapError("");
      if (!googleKey) {
        setMapError(t('google_maps_key_missing'));
        return;
      }

      if (origin.lat == null || origin.lng == null) {
        setMapError(t('pickup_coordinates_missing'));
        return;
      }

      if (destination.lat == null || destination.lng == null) {
        setMapError(t('delivery_coordinates_missing'));
        return;
      }

      await loadGoogleMapsScript(googleKey);
      if (cancelled) return;
      if (!mapRef.current) return;

      // Dialog content can mount with 0-size during animation/layout.
      // Retry a few times before showing an error.
      if (!containerHasSize(mapRef.current)) {
        retryRef.current += 1;
        if (retryRef.current <= 40) {
          setTimeout(() => {
            if (!cancelled) initMapAndRoute();
          }, 100);
          return;
        }
        setMapError(t('map_render_error'));
        return;
      }

      // Reuse map instance across rerenders to avoid blank-canvas issues.
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          zoom: 11,
          center: origin,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        });
      }

      const map = mapInstanceRef.current;
      const directionsService = new window.google.maps.DirectionsService();

      if (!directionsRendererRef.current) {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: "#2563EB", // amber like screenshot
            strokeWeight: 5,
          },
        });
      } else {
        directionsRendererRef.current.setMap(map);
      }

      const directionsRenderer = directionsRendererRef.current;

      // Build waypoints array - include DB if available
      const waypoints = [];
      if (dbLocation.lat != null && dbLocation.lng != null) {
        waypoints.push({
          location: dbLocation,
          stopover: true,
        });
      }

      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (cancelled) return;
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            clearTrackingPolyline();
            directionsRenderer.setDirections(result);
            ensureStaticMarkers(map);
            let bounds = result.routes?.[0]?.bounds;
            if (
              trackingMapPoints.length >= 2 &&
              window.google?.maps?.Polyline
            ) {
              trackingPolylineRef.current = new window.google.maps.Polyline({
                path: trackingMapPoints,
                geodesic: true,
                strokeColor: "#065C36",
                strokeOpacity: 0.88,
                strokeWeight: 4,
                map,
                zIndex: 2,
              });
              const extended =
                bounds != null
                  ? bounds
                  : new window.google.maps.LatLngBounds();
              trackingMapPoints.forEach((pt) => extended.extend(pt));
              bounds = extended;
            }
            if (bounds) {
              // ensure map is properly painted after dialog layout
              window.google.maps.event.trigger(map, "resize");
              map.fitBounds(bounds);
              setTimeout(() => {
                if (!cancelled) {
                  window.google.maps.event.trigger(map, "resize");
                  map.fitBounds(bounds);
                }
              }, 120);
            }
            if (!cancelled) {
              setMapRouteNonce((n) => n + 1);
            }
          } else {
            setMapError(t('map_route_compute_error'));
          }
        }
      );
    }

    initMapAndRoute().catch(() => { // This is a developer-facing error message.
      if (!cancelled) setMapError("Could not load Google Maps route.");
    });

    return () => {
      cancelled = true;
      if (trackingPolylineRef.current) {
        trackingPolylineRef.current.setMap(null);
        trackingPolylineRef.current = null;
      }
      clearStaticMarkers();
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      if (liveMarkerRef.current) {
        liveMarkerRef.current.setMap(null);
        liveMarkerRef.current = null;
      }
    };
  }, [
    googleKey,
    dbLocation.lat,
    dbLocation.lng,
    origin.lat,
    origin.lng,
    destination.lat,
    destination.lng,
    trackingMapPoints,
  ]);

  // Simple keyword-based highlighting; API messages differ across environments.
  const timelineText = useMemo(() => {
    if (!timeline) return "";
    if (typeof timeline === "string") return timeline;
    try {
      return JSON.stringify(timeline).toLowerCase();
    } catch {
      return "";
    }
  }, [timeline]);

  const stepCompleted = (re) => re.test(timelineText);

  const currentTimelineStep = useMemo(() => {
    const v = timelineOverride ?? deliveryPartner?.delivery_timeline;
    if (v == null) return null;
    if (typeof v === "number") return v;
    const key = String(v).trim().toLowerCase();
    return timelineMap[key] ?? null;
  }, [deliveryPartner?.delivery_timeline, timelineOverride]);

  const stepDotClass = (stepNo) => {
    if (currentTimelineStep != null) {
      return stepNo <= currentTimelineStep ? "bg-[#065C36]" : "bg-gray-300";
    }
    // fallback (older behavior) if delivery_timeline isn't present
    return null;
  };

  const stepLineClass = (fromStepNo) => {
    if (currentTimelineStep != null) {
      return fromStepNo < currentTimelineStep ? "bg-[#065C36]" : "bg-gray-200";
    }
    return "bg-gray-200";
  };

  const onUpdateStatus = () => {
    if (selectedAction == null) {
      return;
    }
    if (deliveryPartner?.delivery_timeline !== 'verification') {
      Toaster.error(t('delivery_not_completed_error'));
      return;
    }

    dispatch(updateDeliveryStatus({rfq_product_id:deliveryPartner?.rfq_product_id, status:'delivered'})).unwrap().then((res)=>{
      if(res?.status){
        Toaster.success(res?.message);
        onClose();
      }else{
        Toaster.error(ct('something_went_wrong'))
      }
    });
  }

  // return (
  //   <Dialog open={true} handler={onClose} size="xl" className="rounded-2xl p-0">
  //     {loading && <ShowLoader />}
  //     <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
  //       <div className="flex items-center gap-3">
  //         <h2 className="text-2xl font-bold text-gray-900">Delivery Timeline</h2>
  //         {hasDeliveryUpdatePermission && (
  //           deliveryPartner?.delivery_timeline !== 'delivered' && (
  //             <Menu placement="bottom-start">
  //               <MenuHandler>
  //                 <Button className="flex items-center gap-2 rounded-full bg-black text-white px-4 py-2 text-sm font-semibold capitalize shadow-none whitespace-nowrap">
  //                   {selectedAction ? 'Reviewed & Delivered' : 'Update Status'}
  //                   <svg
  //                     width="14"
  //                     height="14"
  //                     viewBox="0 0 20 20"
  //                     fill="currentColor"
  //                     aria-hidden="true"
  //                     className="opacity-90"
  //                   >
  //                     <path
  //                       d="M5.5 7.5 10 12l4.5-4.5"
  //                       stroke="currentColor"
  //                       strokeWidth="2"
  //                       fill="none"
  //                       strokeLinecap="round"
  //                       strokeLinejoin="round"
  //                     />
  //                   </svg>
  //                 </Button>
  //               </MenuHandler>
  //               <MenuList className="p-1 z-[9999]">
  //                 {actionMenuOptions.map((option) => (
  //                   <MenuItem
  //                     key={option.value}
  //                     className="text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
  //                     onClick={() => {
  //                       setSelectedAction(option.value);
  //                       setTimelineOverride(option.value);
  //                     }}
  //                   >
  //                     {option.label}
  //                   </MenuItem>
  //                 ))}
  //               </MenuList>
  //             </Menu>
  //           )
  //         )}
  //       </div>
  //       <button
  //         onClick={onClose}
  //         className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
  //       >
  //         <XMarkIcon className="w-6 h-6" />
  //       </button>
  //     </DialogHeader>

  //     <DialogBody className="px-6 py-6">
  //       <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-6 items-start">
  //         <div>
  //           <div className="text-sm font-semibold text-gray-600 mb-2">
  //             Logistics & Shipping
  //           </div>

  //           <ul className="space-y-4">
  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(1) ??
  //                     (stepCompleted(/out for pick|pick up|pickup/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //                 <span className={`mt-1 h-9 w-px ${stepLineClass(1)}`} />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">
  //                   Out for Pick Up
  //                 </div>
  //                 <div className="text-xs text-gray-600">
  //                   Delivery Agent is out for Delivery
  //                 </div>
  //               </div>
  //             </li>

  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(2) ??
  //                     (stepCompleted(/picked up|in transit/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //                 <span className={`mt-1 h-9 w-px ${stepLineClass(2)}`} />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">
  //                   Picked Up / In Transit
  //                 </div>
  //                 <div className="text-xs text-gray-600">
  //                   Your order has been picked up by our Delivery Agent
  //                 </div>
  //               </div>
  //             </li>

  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(3) ??
  //                     (stepCompleted(/arrived|destination/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">
  //                   Arrived at Destination
  //                 </div>
  //                 <div className="text-xs text-gray-600">
  //                   The delivery agent has reached the location
  //                 </div>
  //               </div>
  //             </li>
  //           </ul>

  //           <div className="my-6 border-t border-gray-200" />

  //           <div className="text-sm font-semibold text-gray-600 mb-2">
  //             Verification & Fulfillment
  //           </div>

  //           <ul className="space-y-4">
  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(4) ??
  //                     (stepCompleted(/verification/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //                 <span className={`mt-1 h-9 w-px ${stepLineClass(4)}`} />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">
  //                   Delivery Verification
  //                 </div>
  //                 <div className="text-xs text-gray-600">
  //                   Delivery Agent completes the verification process
  //                 </div>
  //               </div>
  //             </li>

  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(5) ??
  //                     (stepCompleted(/under review|review/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //                 <span className={`mt-1 h-9 w-px ${stepLineClass(5)}`} />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">
  //                   Under Review
  //                 </div>
  //                 <div className="text-xs text-gray-600">
  //                   The QR and Sales Representative validate the uploaded delivery files
  //                 </div>
  //               </div>
  //             </li>

  //             <li className="flex items-start gap-3">
  //               <div className="relative flex flex-col items-center">
  //                 <span
  //                   className={`mt-1 h-2.5 w-2.5 rounded-full ${
  //                     stepDotClass(6) ??
  //                     (stepCompleted(/delivered/i)
  //                       ? "bg-amber-500"
  //                       : "bg-gray-300")
  //                   }`}
  //                 />
  //               </div>
  //               <div>
  //                 <div className="font-semibold text-gray-900">Delivered</div>
  //                 <div className="text-xs text-gray-600">
  //                   Sales Representative marks the order as &quot;Delivered&quot;
  //                 </div>
  //               </div>
  //             </li>
  //           </ul>

  //         </div>

  //         <div className="rounded-2xl border border-gray-200 overflow-hidden">
  //           <div className="h-96 bg-gray-50 relative">
  //             {loading && (
  //               <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
  //                 Loading timeline...
  //               </div>
  //             )}

  //             {!loading && mapError && (
  //               <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
  //                 <div className="text-sm text-gray-600">{mapError}</div>
  //               </div>
  //             )}

  //             <div ref={mapRef} className="h-96 w-full" aria-label="Route map" />
  //           </div>
  //         </div>
  //       </div>
  //     </DialogBody>

  //     {hasDeliveryUpdatePermission && deliveryPartner?.delivery_timeline !== 'delivered'  && (
  //     <DialogFooter className="flex justify-end gap-3 border-t pt-4">
  //       <Button variant="text" color="gray" onClick={onClose}>
  //         Not now
  //       </Button>
  //       <Button variant="filled" color="amber" onClick={()=>onUpdateStatus()}>
  //         Confirm
  //       </Button>
  //     </DialogFooter>
  //     )}
  //   </Dialog>
  // );

  return (
    <Dialog
      open={true}
      handler={onClose}
      size="xl"
      className="rounded-2xl p-0 max-h-[90vh] overflow-hidden flex flex-col"
    >
      {loading && <ShowLoader />}

      {/* HEADER (fixed) */}
      <DialogHeader className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('delivery_timeline_title')}
          </h2>

          {hasDeliveryUpdatePermission &&
            deliveryPartner?.delivery_timeline !== "delivered" && 
            deliveryPartner?.delivery_proofs?.length > 0 &&(
              <Menu placement="bottom-start">
                <MenuHandler>
                  <Button className="flex items-center gap-2 rounded-full bg-black text-white px-4 py-2 text-sm font-semibold capitalize shadow-none whitespace-nowrap">
                    {selectedAction
                      ? t('reviewed_and_delivered_action')
                      : t('update_status_button')}
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      className="opacity-90"
                    >
                      <path
                        d="M5.5 7.5 10 12l4.5-4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </MenuHandler>

                <MenuList className="p-1 z-[9999]">
                  {actionMenuOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      className="text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
                      onClick={() => {
                        setSelectedAction(option.value);
                        setTimelineOverride(option.value);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
        </div>

        <button
          onClick={onClose}
          className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </DialogHeader>

      {/* BODY (scrollable) */}
      <DialogBody className="px-6 py-6 overflow-y-auto flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-6 items-start">
          
          {/* LEFT SIDE */}
          <div>
            <div className="text-sm font-semibold text-gray-600 mb-2">
              {t('logistics_and_shipping_title')}
            </div>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(1) ??
                      (stepCompleted(/out for pick|pick up|pickup/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                  <span className={`mt-1 h-9 w-px ${stepLineClass(1)}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('out_for_pickup_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('out_for_pickup_description')}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(2) ??
                      (stepCompleted(/picked up|in transit/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                  <span className={`mt-1 h-9 w-px ${stepLineClass(2)}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('picked_up_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('picked_up_description')}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(3) ??
                      (stepCompleted(/arrived|destination/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('arrived_at_destination_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('arrived_at_destination_description')}
                  </div>
                </div>
              </li>
            </ul>

            <div className="my-6 border-t border-gray-200" />

            <div className="text-sm font-semibold text-gray-600 mb-2">
              {t('verification_and_fulfillment_title')}
            </div>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(4) ??
                      (stepCompleted(/verification/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                  <span className={`mt-1 h-9 w-px ${stepLineClass(4)}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('delivery_verification_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('delivery_verification_description')}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(5) ??
                      (stepCompleted(/under review|review/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                  <span className={`mt-1 h-9 w-px ${stepLineClass(5)}`} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('under_review_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('under_review_description')}
                  </div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <div className="relative flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${
                      stepDotClass(6) ??
                      (stepCompleted(/delivered/i)
                        ? "bg-amber-500"
                        : "bg-gray-300")
                    }`}
                  />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {t('delivered_status')}
                  </div>
                  <div className="text-xs text-gray-600">
                    {t('delivered_description')}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* RIGHT SIDE MAP */}
          <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <div className="h-96 bg-gray-50 relative">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                  {t('loading_timeline')}
                </div>
              )}

              {!loading && mapError && (
                <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                  <div className="text-sm text-gray-600">{mapError}</div>
                </div>
              )}

              <div
                ref={mapRef}
                className="h-96 w-full"
                aria-label="Route map"
              />
            </div>
          </div>
        </div>
      </DialogBody>

      {/* FOOTER (fixed) */}
      {hasDeliveryUpdatePermission &&
        deliveryPartner?.delivery_timeline !== "delivered" && (
          <DialogFooter className="flex justify-end gap-3 border-t pt-4 flex-shrink-0">
            <Button variant="text" color="gray" onClick={onClose}> 
              {t('not_now_button')}
            </Button>
            <Button
              variant="filled"
              color="amber"
              onClick={() => onUpdateStatus()}
            >
              {t('confirm_button')}
            </Button>
          </DialogFooter>
        )}
    </Dialog>
  );
}
